import { KenticoRulesCreator } from './KenticoRulesCreator';
import { StringHelpers } from '../../helpers/StringHelpers';
import { KenticoRulesHelper } from '../KenticoRulesHelper';
import { getResourceString } from '../resource-strings';
import { KenticoFormControlHelpers } from '../helpers/KenticoFormControlHelpers';
import { 
  IFormFieldDefinition,
  ICheckboxMultipleFieldDefinition, 
  IField, 
  IFieldOverrides, 
  IContributionBlockFieldSettings, 
  ICtaFieldSettings, 
  ICustomTemplateFieldSettings, 
  ILoginFieldSettings, 
  IPetitionFieldSettings, 
  IRichTextFieldSettings, 
  ITextAreaFieldSettings, 
  ITipFieldSettings, 
  IAlertFieldSettings,
  IReCaptchaFieldSettings,
  IDropdownSettings,
  IDropdownMultipleSettings,
  IListFieldSettings,
  IRadioSettings,
  ICheckboxMultipleSettings,
  IQrCodeScannerFieldDefinition,
  IQrCodeScannerSettings
} from '../../interfaces/Fields';
import { MacroHelper } from '../helpers/macro-helper';
import { Logger } from '@fnv-core/logging';
import { OptionsParser } from './OptionsParser';

export class FormControlBuilder {
  static category(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const categoryFieldOverrides = FormControlBuilder.getFormFieldOverridesForField<{ skip: boolean }>(field, formFieldOverrides);

    if (categoryFieldOverrides.skip === true) {
      return;
    }

    control.type = 'title';
    control.cssClass = 'section__start';
    control.groupCssClass = "";
    if (field.settings && field.settings.GroupStyling && field.settings.GroupStyling !== "standard") {
      control.groupCssClass = "form-section__" + field.settings.GroupStyling;
    }

    if (field.settings && field.settings.Collapsable) {
      control.collapsable = (field.settings.Collapsable || "").toLowerCase() === "true";
      control.initialCollapsableState = (field.settings.InitialCollapsableState || "").toLowerCase() === "true";
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static dropdown(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {

    const settings = field.settings as IDropdownSettings;

    control.type = 'dropdown';
    control.placeholder = getResourceString('Form.Dropdown.ChooseOption');

    const { values, labels } = OptionsParser.parseOptionsStringOld(field.settings.Options);
    control.options = labels;
    control.valueoptions = values;

    const formConfigOverrides = FormControlBuilder.getFormFieldOverridesForField<{ defaultSelection: boolean }>(field, formFieldOverrides);
    if (formConfigOverrides.defaultSelection && values.length) {
      control.defaultValue = values[0];
    }

    control.options = labels;
    control.valueoptions = values;

    control.sortItems = settings.SortItems;

    control.PicklistMode = settings.PicklistMode || '';
    control.PicklistTemplate = settings.PicklistTemplate || null;

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);

    return control;
  }

  static dropdownMultiple(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {

    FormControlBuilder.dropdown(field, control, formFieldOverrides);

    const settings = field.settings as IDropdownMultipleSettings;

    control.type = 'dropdownmultiple';

    control.addMoreLabel = settings.AddMoreLabel;
    control.maxItems = typeof (settings.MaxItems) === 'string' ? parseInt(settings.MaxItems) : null;
    control.displayMode = settings.DisplayMode;
    control.sortItems = settings.SortItems === 'True';
    control.dataType = 'array';

    // Note: This should be the same separator as the backend expects, which currently is the comma
    control.separator = ',';

    return control;
  }

  static checkbox(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'checkbox';
    control.optionyes = field.properties?.fieldcaption ?? "";
    control.valueyes = 'true';
    control.checkBoxLabel = field.settings && field.settings.Text ? field.settings.Text : "";

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static checkboxMultiple(field: IField, formControl: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ICheckboxMultipleSettings;
    let control = formControl as ICheckboxMultipleFieldDefinition;

    control.type = 'checkboxMultiple';
    control.sortItems = settings.SortItems === 'True';
    control.selectAll = settings.SelectAll === 'True';
    control.separator = settings.Separator;
    control.selectAllText = typeof settings.SelectAllText === 'string' ? settings.SelectAllText : settings.SelectAllText?.['#text'];
    control.dataType = 'array';

    const { values, labels } = OptionsParser.parseOptionsStringOld(settings.Options);
    control.options = labels;
    control.valueoptions = values;

    const formConfigOverrides = FormControlBuilder.getFormFieldOverridesForField<{ defaultSelection: boolean }>(field, formFieldOverrides);
    if (formConfigOverrides.defaultSelection && control.options.length) {
      control.defaultValue = control.options[0];
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    var mergedFormControl = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    control = mergedFormControl as ICheckboxMultipleFieldDefinition;
    return control;
  }

  static switch(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'switch';
    control.optionyes = field.properties.fieldcaption;
    control.valueyes = 'true';
    control.checkBoxLabel = field.settings && field.settings.Text ? field.settings.Text : "";

    if (field["settings"] && 
        field["settings"]["ShowSwitchInDisabledState"] !== undefined && 
        field["settings"]["ShowSwitchInDisabledState"].toLowerCase() == "true") {
      (control as any).showSwitchInDisabledState = true; 
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static button(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'button';
    control.icon = field.settings && field.settings.Icon;
    control.cssClass = "btn-primary button-form-control__post";
    control.buttonMode = "post";
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }    
  
  static radio(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IRadioSettings;

    control.type = 'radio';
    control.subtype = settings.controlname;

    const { values, labels, icons } = OptionsParser.parseOptionsStringOld(settings.Options);
    control.options = labels;
    control.valueoptions = values;
    control.iconoptions = icons;

    if (field.properties?.inputcontrolstyle) {
      control.inputcontrolstyle = field.properties.inputcontrolstyle;
    }

    if (settings.CorrectAnswer != null) {
      control.correctAnswer = settings.CorrectAnswer;
    } else {
      control.correctAnswer = false;
    }

    if (settings.ShowRadioButtonInDisabledState == 'True') {
      control.showRadioButtonInDisabledState = true; 
    }

    // settings.RepeatDirection : horizontal vertical
    if (settings.RepeatDirection) {
      control.cssClass = 'radio-buttons-' + settings.RepeatDirection;
    }

    // RepeatDirection can be overriden through form config
    const radioFieldOverrides = FormControlBuilder.getFormFieldOverridesForField<{ repeatDirection: string }>(field, formFieldOverrides);
    if (radioFieldOverrides.repeatDirection) {
      control.cssClass = 'radio-buttons-' + formFieldOverrides[control.name].repeatDirection;
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static readOnly(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'readOnly';

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static text(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';

    if (field["@columntype"] ===  "decimal") {
      control.dataType = 'decimal';
    }
    
    if (field["properties"] && field["properties"]["inputcontrolstyle"]) {
      control.inputcontrolstyle = field["properties"]["inputcontrolstyle"];
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);

    const controlNameLowercase = (control.name || "").toLowerCase();

    if (controlNameLowercase === "voornaam" || controlNameLowercase === "achternaam") {
      control.maskCharsExpression = KenticoFormControlHelpers.getRegexForNameFields();
    }
    return control;
  }

  static qrcodescanner(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IQrCodeScannerSettings;
    let qrcontrol = control as IQrCodeScannerFieldDefinition;

    qrcontrol.type = 'qrcodescanner';
    qrcontrol.isUsedToScanJson = settings.IsUsedToScanJson === "True";

    if (field["properties"] && field["properties"]["inputcontrolstyle"]) {
      qrcontrol.inputcontrolstyle = field["properties"]["inputcontrolstyle"];
    }

    qrcontrol = KenticoRulesCreator.assignKenticoRules(field, control);
    qrcontrol = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return qrcontrol;
  }

  static phonenumber(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';
    control.maxlength = 15;
    if (field["properties"] && field["properties"]["inputcontrolstyle"]) {
      control.inputcontrolstyle = field["properties"]["inputcontrolstyle"];
    }
    control.maskCharsExpression = "^[\+0-9]+$";
    control.hasNumericKeyboard = true;
    let kenticoValidationRules = [];
    try {
      const validationFunctionFromKenticoRule = KenticoRulesHelper.getValidationMethodFromRegex(
        'Value.Matches("^(((\\\\+|00)31)|0)[1-9]\\\\d{8}$|^(\\\\+|00)(?!31)[1-9]\\\\d{6,14}$")'
      );

      kenticoValidationRules.push({
        error: StringHelpers.formFieldPhoneNumberErrorMessage(control.label) || 
              (field.properties && field.properties.validationerrormessage) || "Error",
        validationFunction: validationFunctionFromKenticoRule
      });
    } catch (err) {
      // skip validation rule
      // tslint:disable-next-line
      console.log(err);
    }

    if (kenticoValidationRules.length > 0) {
      control.validation.kenticoRules = {
        rules: kenticoValidationRules
      };
    } 

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static numericinputfield(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';
    
    // @columnsize is actually a string type, but the typescript intellisense does not allow < operators on strings so act like it is a number instead
    if ((field['@columnsize'] as unknown as number) < 10) {
      control.maxlength = field['@columnsize'];
    } else {
      control.maxlength = 10;
    }
    
    control.alwaysUppercase = true;
    if (field.settings.AllowDecimals === undefined || field.settings.AllowDecimals == "True") {
      control.maskCharsExpression = "^[0-9]+,?[0-9]*$";
      control.allowDecimals = true;
    } else {
      control.maskCharsExpression = "^[0-9]+$";
      control.allowDecimals = false;
    }
    
    control.isNumeric = true;
    control.hasNumericKeyboard = true;
    // ^\d{0,2}(\,\d{1,2})?$       
    if (field.settings.Min && field.settings.Max &&
        !isNaN(parseInt(field.settings.Min)) && 
        !isNaN(parseInt(field.settings.Max)) && 
        parseInt(field.settings.Min) >= 0 && parseInt(field.settings.Max) > 0) {

      const minAsNumber = parseFloat(field.settings.Min);
      const maxAsNumber = parseFloat(field.settings.Max);
      const minHasDecimals = (minAsNumber - Math.floor(minAsNumber)) !== 0;
      const maxHasDecimals = (maxAsNumber - Math.floor(maxAsNumber)) !== 0;

      let minDecimalPlaces = 2;
      let maxDecimalPlaces = 2;
      
      if (!minHasDecimals) {
        minDecimalPlaces = 0;            
      }
      
      if (!maxHasDecimals) {
        maxDecimalPlaces = 0;            
      }
      
      let minmaxerror = StringHelpers.formFieldMinMaxErrorMessage(control.label, 
        minAsNumber.toFixed(minDecimalPlaces).replace("." , ","), 
        maxAsNumber.toFixed(maxDecimalPlaces).replace("." , ",")
      );

      if (maxAsNumber === 99999) {
        minmaxerror = StringHelpers.formFieldMinMaxNoMaxErrorMessage(control.label, 
          minAsNumber.toFixed(minDecimalPlaces).replace("." , ",")
        );
      }

      control.validation.dataType = {
        kind: 'number',
        error: StringHelpers.formFieldGenericErrorMessage(control.label),
        min: minAsNumber,
        max: maxAsNumber,
        allowDecimals: control.allowDecimals,
        minmaxerror: minmaxerror,
      };
    }

    let kenticoValidationRules = [];
    try {
      let validationFunctionFromKenticoRule = KenticoRulesHelper.getValidationMethodFromRegex(
        'Value.Matches("^(0|[1-9](\\\\d{0,2})?)(\\\\,\\\\d{1,2})?$")'
      );
      let error = `Het veld '${control.label}' is geen geldig decimaal getal`;

      const numericInputFormOverrides = FormControlBuilder.getFormFieldOverridesForField<{ onlyExactHalf: boolean }>(field, formFieldOverrides);
      if (numericInputFormOverrides.onlyExactHalf) {
        validationFunctionFromKenticoRule = KenticoRulesHelper.getValidationMethodFromRegex(
          'Value.Matches("^\\\\d{1,2}(\\\\,[5|0][0]?)?$")'
        );

        error = `Het veld '${control.label}' is geen geldig aantal uur afgerond op halve uren`;
      }

      kenticoValidationRules.push({
        error: error,
        validationFunction: validationFunctionFromKenticoRule
      });
    } catch (err) {
      // skip validation rule
      // tslint:disable-next-line
      console.log(err);
    }

    if (kenticoValidationRules.length > 0) {
      control.validation.kenticoRules = {
        rules: kenticoValidationRules
      };
    }  
    
    if (field["properties"] && field["properties"]["inputcontrolstyle"]) {
      control.inputcontrolstyle = field["properties"]["inputcontrolstyle"];
    }         

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static intialsinputfield(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';
    control.maxlength = 30;
    control.alwaysUppercase = true;
    control.maskCharsExpression = KenticoFormControlHelpers.getRegexForNameFields();
    if (!control.explanation || control.explanation == "") {
      control.explanation = getResourceString("Form.Initials.Hint");
      control.hasPopover = true;
    }

    if (field["properties"] && field["properties"]["inputcontrolstyle"]) {
      control.inputcontrolstyle = field["properties"]["inputcontrolstyle"];
    }

    let kenticoValidationRules = [];
    try {
      // De 4 dubbele / is nodig omdat anders de regulier expressie alleen de . bevat
      // door hoe de kentico validatie regels worden opgebouwd, wordt een string meerdere keren "geunscaped" (zie kenticoruleshelper)
      const regex = `Value.Matches("^([A-Z]+|([A-Z]+\\\\.?)+)$")`;
      const validationFunctionFromKenticoRule = KenticoRulesHelper.getValidationMethodFromRegex(
        regex
      );
      kenticoValidationRules.push({
        // Form.Initials.Error
        error: StringHelpers.formFieldInititalsErrorMessage(control.label) || "Error",
        validationFunction: validationFunctionFromKenticoRule
      });
    } catch (err) {
      // skip validation rule
      // tslint:disable-next-line
      console.log(err);
    }

    if (kenticoValidationRules.length > 0) {
      control.validation.kenticoRules = {
        rules: kenticoValidationRules
      };
    }  

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static fileupload(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'fileupload';
    control.fileFormats = (field.settings["Allowed_Extensions"] || "").split(";");
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static email(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';

    control.validation.email = {
      error: StringHelpers.formFieldGenericErrorMessage(control.label) + " " + getResourceString("Form.Email.InvalidError"),
    };
    
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static emailAddressList(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'emailaddresslist';
    control.emailSeparator = ";";
    control.emailError = StringHelpers.formFieldGenericErrorMessage(control.label) + " " + getResourceString("Form.Email.InvalidError");
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static iban(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'text';
    control.maskCharsExpression = "^[0-9a-zA-Z]+$";
    control.allowPrefixSpacesInInputOnly = true;

    control.validation.dataType = {
      kind: 'iban',
      error: StringHelpers.formFieldGenericErrorMessage(control.label) + " " + getResourceString("Form.Iban.InvalidError"),        
    };
    
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static textarea(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ITextAreaFieldSettings;

    control.type = 'textarea';

    if (settings && 
        settings.ShowTextAreaInDisabledState !== undefined && 
        settings.ShowTextAreaInDisabledState.toLowerCase() == "true") {
      control.showTextAreaInDisabledState = true; 
    }

    if (settings.Size !== undefined){
      control.maxlength = parseInt(settings.Size);
    }

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static password(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'password';
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static datetime(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'datepicker';
    control.maxlength = 10;
    control.dataType = 'date';
    control.mask = '99-99-9999';
    control.validation.dataType = {
      kind: 'date',
      error: StringHelpers.formFieldGenericErrorMessage(control.label),
      minage: 0,
      minyear: 1900
    };

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static popupConfirm(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'custom';
    control.customType = 'popupConfirm';

    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static popupOk(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'custom';
    control.customType = 'popupOk';
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static link(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'link';

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static grid(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'custom';
    control.customType = 'grid';

    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);

    return control;
  }

  static list(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {

    const settings = field.settings as IListFieldSettings;

    control.type = 'custom';
    control.customType = 'list';

    try {
      const definition = JSON.parse(settings.ListDefinition) as {
        columns: { title: string, property: string, formatter?: string }[]
      };
      control.columns = definition.columns;
    } catch (error) {
      Logger.error(error);
      control.columns = [];
    }

    control.noDataText = settings.NoDataText;

    return control;
  }

  static contributieBlock(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IContributionBlockFieldSettings;

    control.type = 'custom';
    control.customType = "contributie";
    control.title = settings.Title || "";
    control.periodText = settings.PeriodText || "";
    control.beforeText = settings.BeforeText || "";
    control.afterText = settings.AfterText ||  "";
    control.toonLidWordenButton = settings.ShowMembershipButton === "True"; 
    control.lidWordenButtonTitle = settings.MembershipButtonCaption || "";
    control.toonLidVoordelen = settings.ShowAdvantagesMembership == "True";
    control.lidVoordelenRichtText = settings.MembershipAdvantages || "";
    control.lidVoordelenTitle = settings.MembershipAdvantagesTitle || "";
    control.lidVoordelenBeforeText = settings.MembershipAdvantagesIntro || "";
    control.hintContributieTarief = settings.MembershipFeeHint || "";
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static tipBlock(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ITipFieldSettings;

    control.type = 'custom';
    control.customType = 'tip';
    control.tip = settings.Tip || '';
    control.tipIcon = settings.Icon || '';
    control.tipLayout = settings.Layout || 'callout';
    
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static alertBlock(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IAlertFieldSettings;

    control.type = 'custom';
    control.customType = 'alert';
    control.alert = settings.Alert || '';
    control.alertClass = settings.AlertType || '';
    
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static horizontalRuler(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'custom';
    control.customType = "horizontal-ruler";        
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static richTextEditor(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'richtexteditor';
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static richTextContent(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IRichTextFieldSettings;

    control.type = 'custom';
    control.customType = 'richtextcontent';
    control.content = settings.Content || '';
    let newField = {...field};
    if (newField.properties !== undefined && !newField.properties.visiblemacro) {
      if (settings.showOnSummaryStep) {
        MacroHelper.setVisibilityMacro(newField, 'Utils.IsSummaryView()');
      } else {
        MacroHelper.setVisibilityMacro(newField, '!Utils.IsSummaryView()');
      }
    }
    if (settings.PlaceInFormArea) {
      control.PlaceInFormArea = settings.PlaceInFormArea.toLowerCase() == "true";
    }

    control = KenticoRulesCreator.assignKenticoRules(newField, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static customTemplate(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ICustomTemplateFieldSettings;

    control.type = 'custom';
    control.customType = "richtextcontent";
    control.content = settings.CustomTemplate || "";
    let newField = {...field};

    control.templateType = settings.TemplateType || ""; 
    control = KenticoRulesCreator.assignKenticoRules(newField, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }
  
  static ctaBlock(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ICtaFieldSettings;

    control.type = 'custom';
    control.customType = "ctablock";

    control.ctaTitle = settings.CtaTitle || "";
    control.ctaDescription = settings.CtaDescription || "";
    control.ctaButtonLink = settings.CtaButtonLink || "";
    control.ctaButtonText = settings.CtaButtonText || "";

    let newField = {...field};
    if (newField.properties !== undefined && !newField.properties.visiblemacro) {
      if (!settings.showOnSummaryStep) {
        MacroHelper.setVisibilityMacro(newField, '!Utils.IsSummaryView()');
      }
    }
    control = KenticoRulesCreator.assignKenticoRules(newField, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static headingTwo(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'custom';
    control.customType = 'titleHeadingTwo';

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static chooseLocation(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    control.type = 'chooseLocation';

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static petitieTeller(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IPetitionFieldSettings;

    control.type = 'custom';
    control.customType = 'petitieTeller';
    
    control.maxPetitionCount = settings.MaxPetitionCount ? parseFloat(settings.MaxPetitionCount) : 50000;
    control.StartValuePetitionCount = settings.StartValuePetitionCount ? parseFloat(settings.StartValuePetitionCount) : 0;

    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);
    return control;
  }

  static loginRedirect(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as ILoginFieldSettings;

    control.type = 'custom';
    control.customType = 'loginRedirect';

    let newField = {...field};
    MacroHelper.setVisibilityMacro(newField, '!Utils.IsAuthenticated()');

    control.linkText = settings?.LinkText || 'Log in';

    control = KenticoRulesCreator.assignKenticoRules(newField, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);

    return control;
  }

  static reCaptcha(field: IField, control: IFormFieldDefinition, formFieldOverrides: IFieldOverrides) {
    const settings = field.settings as IReCaptchaFieldSettings;
    
    control.type = 'reCaptchaV2Invisible';

    control.siteKey = settings.SiteKey;
    
    control = KenticoRulesCreator.assignKenticoRules(field, control);
    control = KenticoRulesCreator.mergeFormDefinition(control, formFieldOverrides);

    return control;
  }

  /**
   * Gets the FormConfig overrides for the given field.
   * 
   * @param field                 The field to get the overrides for.
   * @param formFieldOverrides    The object containing all overrides.
   * 
   * @returns                     An object containing the overrides for the given field. This object is never null or undefined.
   */
  static getFormFieldOverridesForField<TOverrides extends { [key: string]: any }>(field: IField, formFieldOverrides: IFieldOverrides): TOverrides {
    if (!formFieldOverrides) {
      return {} as TOverrides;
    }

    const overridesForField = formFieldOverrides[field['@column']];
    if (!overridesForField) {
      return {} as TOverrides;
    }

    return overridesForField as TOverrides;
  }
}
